import { countsTowardActionNeeded, type ChaseCategory, type ChaseSource, type ChaseStakes, type OpenAsksRead, type WebOpenAsk } from './open-asks';

// The pure half of the Action needed tab's "While you were away" group (docs/2026-10-23-chaser-design.md §10;
// plan 3.6): the chaser's OWED items as `/api/tasks` carries them (`openAsks[].chase`, common-lib `OpenAskChase`),
// their order, and which i18n key each piece of a row renders under. The same rows, words and order as iOS and
// Android. Pure, no runtime imports.

/** A chase ask: an open ask that carries `chase`. */
export type ChaseAsk = WebOpenAsk & { chase: NonNullable<WebOpenAsk['chase']> };

export function isChaseAsk(ask: WebOpenAsk): ask is ChaseAsk {
  return !!ask.chase;
}

const STAKES_RANK: Record<ChaseStakes, number> = { critical: 0, high: 1, normal: 2, low: 3 };
const time = (iso: string | undefined): number => (iso ? Date.parse(iso) || 0 : 0);

/** Stakes (critical first), then due soonest (an undated one last), then the oldest told first — common-lib's order. */
export function compareChaseAsks(a: ChaseAsk, b: ChaseAsk): number {
  const stakes = STAKES_RANK[a.chase.stakes] - STAKES_RANK[b.chase.stakes];
  if (stakes !== 0) return stakes;
  const dueA = a.chase.dueAt ? time(a.chase.dueAt) : Number.POSITIVE_INFINITY;
  const dueB = b.chase.dueAt ? time(b.chase.dueAt) : Number.POSITIVE_INFINITY;
  if (dueA !== dueB) return dueA < dueB ? -1 : 1;
  return time(a.createdDate) - time(b.createdDate);
}

/** The chase asks of a list, in the group's order. */
export function chaseAsksOf(asks: readonly WebOpenAsk[]): ChaseAsk[] {
  return asks.filter(isChaseAsk).sort(compareChaseAsks);
}

/** The read without one chase item — the optimistic removal after "I did it" / "Stop watching". */
export function withoutChaseItem(read: OpenAsksRead | null | undefined, itemId: string): OpenAsksRead | null | undefined {
  if (!read) return read;
  const removed = read.asks.find((ask) => ask.chase?.itemId === itemId);
  if (!removed) return read;
  const asks = read.asks.filter((ask) => ask !== removed);
  if (read.total === undefined) return { asks };
  return { asks, total: Math.max(0, read.total - (countsTowardActionNeeded(removed) ? 1 : 0)) };
}

/**
 * A failed close puts back ONLY that row (and its count share) into whatever the cache holds now — never the
 * snapshot taken before the close, which would undo every refresh and every other close since. Back in the
 * chase group's order; a row the read already holds again is left alone.
 */
export function withChaseAskRestored(read: OpenAsksRead | null | undefined, ask: ChaseAsk): OpenAsksRead | null | undefined {
  if (!read) return read;
  if (read.asks.some((a) => a.chase?.itemId === ask.chase.itemId)) return read;
  const others = read.asks.filter((a) => !isChaseAsk(a));
  const asks = [...others, ...chaseAsksOf([...read.asks, ask])];
  if (read.total === undefined) return { asks };
  return { asks, total: read.total + (countsTowardActionNeeded(ask) ? 1 : 0) };
}

export const CHASE_GROUP_TITLE_KEY = 'tasks.chase.groupTitle';
export const CHASE_DUE_KEY = 'tasks.chase.due';
export const CHASE_ACTION_DONE_KEY = 'tasks.chase.done';
export const CHASE_ACTION_DISMISS_KEY = 'tasks.chase.dismiss';
export const CHASE_ERROR_KEY = 'tasks.chase.error';

/** The badge a row wears: "Urgent" for critical, "Important" for high, none below. */
export function chaseStakesKey(stakes: ChaseStakes): string | null {
  if (stakes === 'critical') return 'tasks.chase.stakes.critical';
  if (stakes === 'high') return 'tasks.chase.stakes.high';
  return null;
}

export const CHASE_SOURCE_KEY: Record<ChaseSource, string> = {
  gmail: 'tasks.chase.source.gmail',
  calendar: 'tasks.chase.source.calendar',
  file: 'tasks.chase.source.file',
};

/** The category glyph's name (a lucide icon), per category. */
export const CHASE_CATEGORY_GLYPH: Record<ChaseCategory, 'wallet' | 'heart' | 'users' | 'briefcase' | 'circle'> = {
  money: 'wallet',
  health: 'heart',
  people: 'users',
  work: 'briefcase',
  other: 'circle',
};

/**
 * The SHORT DATE a "Due {date}" line carries — month and day ("Oct 31"), plus the year only when it is not this
 * year — in the APP's language (`locale`), the same on all three surfaces. Empty on an absent or unparseable date.
 * `now` injectable for tests.
 */
export function chaseDueLabel(iso: string | undefined, now: number = Date.now(), locale?: string): string {
  if (!iso) return '';
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '';
  const date = new Date(at);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  const options: Intl.DateTimeFormatOptions = sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' };
  try {
    return date.toLocaleDateString(locale, options);
  } catch {
    return date.toLocaleDateString(undefined, options);
  }
}
